import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Breadcrumbs, Link, Alert, Skeleton, Paper, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { trpc } from '../../lib/trpc';
import {
  MonitorForm,
  MonitorSubmitData,
  MonitorInitialData,
} from '../../components/monitors/MonitorForm';

/**
 * Loading skeleton for the form
 */
function FormSkeleton() {
  return (
    <Paper sx={{ p: 4, maxWidth: 800 }}>
      <Skeleton variant="text" width="30%" height={32} sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1, flex: 1 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1, flex: 1 }} />
        </Box>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1, flex: 1 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1, flex: 1 }} />
        </Box>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Skeleton variant="rectangular" width={100} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    </Paper>
  );
}

/**
 * Not found component
 */
function MonitorNotFound({ id }: { id: string }) {
  return (
    <Paper sx={{ p: 4, maxWidth: 800, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Monitor Not Found
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        The monitor with ID &quot;{id}&quot; could not be found. It may have been deleted or you may
        not have permission to access it.
      </Typography>
      <Button
        component={RouterLink}
        to="/monitors"
        variant="contained"
        startIcon={<ArrowBackIcon />}
      >
        Back to Monitors
      </Button>
    </Paper>
  );
}

/**
 * Transform API response to form initial data
 */
function transformToInitialData(data: {
  id: string;
  name: string;
  type: 'HTTP' | 'PING' | 'TCP' | 'KEYWORD';
  url: string;
  interval: number;
  timeout: number;
  expectedStatus: number[];
  headers: Record<string, string> | null;
  keyword: string | null;
  keywordType: 'CONTAINS' | 'NOT_CONTAINS' | null;
  checkSsl: boolean;
  sslExpiryWarning: number;
  failureThreshold: number;
}): MonitorInitialData {
  return {
    id: data.id,
    name: data.name,
    type: data.type,
    url: data.url,
    interval: data.interval,
    timeout: data.timeout,
    expectedStatus: data.expectedStatus,
    headers: data.headers,
    keyword: data.keyword,
    keywordType: data.keywordType,
    checkSsl: data.checkSsl,
    sslExpiryWarning: data.sslExpiryWarning,
    failureThreshold: data.failureThreshold,
  };
}

/**
 * Edit monitor page component
 * Form to edit an existing monitor
 */
export function EditMonitorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Fetch monitor data
  const monitorQuery = trpc.monitors.getById.useQuery(
    { id: id! },
    {
      enabled: !!id,
      retry: false,
    }
  );

  // Update monitor mutation
  const utils = trpc.useUtils();
  const updateMutation = trpc.monitors.update.useMutation({
    onSuccess: (data) => {
      // Invalidate queries to refresh data
      utils.monitors.list.invalidate();
      utils.monitors.getById.invalidate({ id: data.id });

      // Navigate back to monitor detail page
      navigate(`/monitors/${data.id}`, {
        state: { message: 'Monitor updated successfully!' },
      });
    },
  });

  const handleSubmit = (data: MonitorSubmitData) => {
    if (!id) return;
    updateMutation.mutate({
      id,
      data,
    });
  };

  const handleCancel = () => {
    // Navigate back to monitor detail or list
    if (id) {
      navigate(`/monitors/${id}`);
    } else {
      navigate('/monitors');
    }
  };

  // Show loading state
  if (monitorQuery.isLoading) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/monitors" color="inherit" underline="hover">
            Monitors
          </Link>
          <Skeleton variant="text" width={100} />
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>

        <Skeleton variant="text" width="40%" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 4 }} />

        <FormSkeleton />
      </Box>
    );
  }

  // Show error state
  if (monitorQuery.error) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/monitors" color="inherit" underline="hover">
            Monitors
          </Link>
          <Typography color="text.primary">Edit Monitor</Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Edit Monitor
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Update monitor settings
        </Typography>

        {monitorQuery.error.data?.code === 'NOT_FOUND' ? (
          <MonitorNotFound id={id || 'unknown'} />
        ) : (
          <Alert severity="error" sx={{ maxWidth: 800 }}>
            Failed to load monitor: {monitorQuery.error.message}
          </Alert>
        )}
      </Box>
    );
  }

  // Show not found if no data
  if (!monitorQuery.data) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/monitors" color="inherit" underline="hover">
            Monitors
          </Link>
          <Typography color="text.primary">Edit Monitor</Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Edit Monitor
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Update monitor settings
        </Typography>

        <MonitorNotFound id={id || 'unknown'} />
      </Box>
    );
  }

  const initialData = transformToInitialData(monitorQuery.data);

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/monitors" color="inherit" underline="hover">
          Monitors
        </Link>
        <Link component={RouterLink} to={`/monitors/${id}`} color="inherit" underline="hover">
          {monitorQuery.data.name}
        </Link>
        <Typography color="text.primary">Edit</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Edit Monitor
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Update settings for &quot;{monitorQuery.data.name}&quot;
      </Typography>

      <MonitorForm
        mode="edit"
        initialData={initialData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={updateMutation.isLoading}
        error={updateMutation.error?.message ?? null}
      />
    </Box>
  );
}

export default EditMonitorPage;
